import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/site";
import { formatHeat, formatDate, formatCount } from "@/utils/format";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";

export default function TopicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const topic = topics.find((t) => t.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title="内容未找到 | 瓜田黑料"
          description="该话题可能已被合并或调整，返回首页查看最新年度盘点与名场面合集。"
          path="/topic"
        />
        <SiteHeader />
        <main className="pt-16 md:pt-20">
          <div className="w-full px-4 md:px-10 py-24 text-center">
            <span className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-background-100 text-4xl text-foreground-400">
              <i className="ri-file-unknow-line" />
            </span>
            <h1 className="mt-6 font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              没有找到这篇内容
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              话题可能已调整或合并，去年度盘点栏目看看最新的整理吧。
            </p>
            <Link
              to="/rankings"
              className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-compass-3-line" />
              前往年度盘点
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = topics.filter((t) => topic.related.includes(t.slug));
  const currentIndex = topics.findIndex((t) => t.slug === topic.slug);
  const prev = currentIndex > 0 ? topics[currentIndex - 1] : null;
  const next = currentIndex < topics.length - 1 ? topics[currentIndex + 1] : null;
  const categoryInfo = categories.find((c) => c.slug === topic.categorySlug);

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${topic.title} | 瓜田黑料`}
        description={`${topic.summary.slice(0, 90)}——瓜田黑料年度话题盘点，含时间线、要点速览与后续追踪。`}
        keywords={`${topic.tags.join(",")},瓜田黑料,年度盘点,TOP榜`}
        path={`/topic/${topic.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: topic.title,
              description: topic.summary,
              articleSection: topic.category,
              inLanguage: "zh-CN",
              datePublished: topic.publishedAt,
              dateModified: topic.publishedAt,
              author: { "@type": "Organization", name: "瓜田黑料编辑部" },
              publisher: { "@type": "Organization", name: "瓜田黑料" },
              mainEntityOfPage: `https://jiemia.cn/topic/${topic.slug}`,
              keywords: topic.tags.join(","),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://jiemia.cn/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "年度盘点 · TOP榜",
                  item: "https://jiemia.cn/rankings",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: topic.title,
                  item: `https://jiemia.cn/topic/${topic.slug}`,
                },
              ],
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-10 pt-8 md:pt-10">
          <Breadcrumb
            items={[
              { name: "首页", path: "/" },
              { name: "年度盘点 · TOP榜", path: "/rankings" },
              { name: topic.category, path: "/rankings" },
              { name: topic.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-10 py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
            <div className="min-w-0">
              <header>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                    {topic.category}
                  </span>
                  {topic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-5 font-heading font-black text-2xl md:text-4xl leading-tight text-foreground-950">
                  {topic.title}
                </h1>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                  {topic.summary}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                  <span>
                    <i className="ri-user-3-line mr-1" />
                    {topic.author}
                  </span>
                  <span>
                    <i className="ri-calendar-line mr-1" />
                    {formatDate(topic.publishedAt)}
                  </span>
                  <span>
                    <i className="ri-time-line mr-1" />
                    阅读约 {topic.readTime}
                  </span>
                  <span>
                    <i className="ri-fire-line mr-1 text-primary-500" />
                    热度 {formatHeat(topic.heat)}
                  </span>
                  <span>
                    <i className="ri-eye-line mr-1" />
                    {formatCount(topic.views)} 次阅读
                  </span>
                </div>
              </header>

              <figure className="mt-8">
                <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={topic.cover}
                    alt={`${topic.title} — 瓜田黑料 ${topic.category} 配图`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-2 text-xs text-foreground-400">
                  配图为抽象示意，仅用于栏目呈现，不代表任何具体事件现场。
                </figcaption>
              </figure>

              <section className="mt-8 rounded-lg bg-accent-50 border border-accent-200 p-5 md:p-6">
                <h2 className="font-heading font-bold text-base text-accent-900 flex items-center gap-2">
                  <i className="ri-list-check-2" />
                  要点速览
                </h2>
                <ul className="mt-3 space-y-2.5">
                  {topic.highlights.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center text-accent-700 mt-0.5 flex-shrink-0">
                        <i className="ri-checkbox-circle-line" />
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </section>

              <div className="mt-10 space-y-10">
                {topic.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index + 1}`}>
                    <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-start gap-3">
                      <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-600 text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph} className="text-sm md:text-base leading-loose text-foreground-700">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 rounded-lg bg-background-100 border border-background-200 p-5">
                <p className="text-xs leading-relaxed text-foreground-500">
                  <i className="ri-shield-check-line mr-1 text-secondary-600" />
                  本文为公开信息整理与复盘，涉及具体事件的表述以当事方公开说明为准。如内容存在疏漏或你有后续进展，欢迎通过
                  <Link to="/about" className="text-primary-600 hover:underline mx-1">
                    线索征集
                  </Link>
                  渠道告诉我们，核实后我们会更新原文。
                </p>
              </div>

              <nav className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {prev ? (
                  <Link
                    to={`/topic/${prev.slug}`}
                    className="group rounded-lg border border-background-200 p-4 hover:border-primary-300 transition-colors"
                  >
                    <span className="text-xs text-foreground-500">
                      <i className="ri-arrow-left-line mr-1" />
                      上一篇
                    </span>
                    <span className="mt-1 block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {prev.title}
                    </span>
                  </Link>
                ) : (
                  <span />
                )}
                {next && (
                  <Link
                    to={`/topic/${next.slug}`}
                    className="group rounded-lg border border-background-200 p-4 sm:text-right hover:border-primary-300 transition-colors"
                  >
                    <span className="text-xs text-foreground-500">
                      下一篇
                      <i className="ri-arrow-right-line ml-1" />
                    </span>
                    <span className="mt-1 block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {next.title}
                    </span>
                  </Link>
                )}
              </nav>
            </div>

            <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5 hidden lg:block">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-3">本文目录</h2>
                <ol className="space-y-2">
                  {topic.sections.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#section-${index + 1}`}
                        className="flex items-start gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                      >
                        <span className="text-xs text-foreground-400 mt-0.5">{index + 1}.</span>
                        {section.heading}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>

              {categoryInfo && (
                <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                  <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-background-50 text-xl text-secondary-700">
                    <i className={categoryInfo.icon} />
                  </span>
                  <h2 className="mt-3 font-heading font-bold text-base text-secondary-900">
                    {categoryInfo.name}
                  </h2>
                  <p className="mt-2 text-xs leading-relaxed text-secondary-800">{categoryInfo.desc}</p>
                  <Link
                    to="/rankings"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-secondary-800 hover:text-secondary-600"
                  >
                    查看该栏目全部内容
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              )}

              {related.length > 0 && (
                <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                  <h2 className="font-heading font-bold text-base text-foreground-950 mb-1">
                    相关推荐
                  </h2>
                  <div className="divide-y divide-background-200">
                    {related.map((item) => (
                      <TopicCard key={item.slug} topic={item} variant="compact" />
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </article>

        {related.length > 0 && (
          <section className="w-full px-4 md:px-10 py-14 bg-background-100">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
              该话题延伸阅读
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((item) => (
                <TopicCard key={item.slug} topic={item} />
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}